import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
} from "@mui/material";

import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import GroupIcon from "@mui/icons-material/Group";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import BoltIcon from "@mui/icons-material/Bolt";
import { CampaignStats } from "./types";

interface ProgressCardProps {
  stats: CampaignStats;
}

const ProgressCard: React.FC<ProgressCardProps> = ({ stats }) => {
  const progressPercentage = Math.min(
    (stats.totalRaised / stats.target) * 100,
    100,
  );

  const amountRemaining = Math.max(stats.target - stats.totalRaised, 0);

  return (
    <Box sx={{ position: "relative" }}>
      <Card
        sx={{
          p: 3,
          borderRadius: 4,
          boxShadow: "0 20px 40px rgba(0,0,0,0.06)",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {/* ===== MONEY SECTION ===== */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h2" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              ${stats.totalRaised.toLocaleString()}
            </Typography>

            <Typography variant="body1" sx={{ color: "text.secondary", mb: 1 }}>
              raised of ${stats.target.toLocaleString()} goal
            </Typography>

            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {Math.round(progressPercentage)}% funded · $
              {amountRemaining.toLocaleString()} left
            </Typography>
          </Box>

          {/* ===== PROGRESS BAR ===== */}
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{
              height: 20,
              borderRadius: 10,
              mb: 4,
              backgroundColor: "#e2e8f0",
              "& .MuiLinearProgress-bar": {
                borderRadius: 10,
                background: "linear-gradient(90deg, #f97316, #fb923c)",
                transition: "width 1s ease-in-out",
              },
            }}
          />

          {/* ===== STATS GRID ===== */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              mb: 3,
            }}
          >
            <Box
              sx={{
                p: 1.5,
                borderRadius: 3,
                border: "1px solid #e5e7eb",
              }}
            >
              <BatteryChargingFullIcon
                sx={{ color: "primary.main", mb: 0.5, fontSize: 22 }}
              />
              <Typography variant="subtitle2">Energy Goal</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                15kW Capacity
              </Typography>
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 3,
                border: "1px solid #e5e7eb",
              }}
            >
              <GroupIcon
                sx={{ color: "success.main", mb: 0.5, fontSize: 22 }}
              />
              <Typography variant="subtitle2">Communities</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                12 Parishes
              </Typography>
            </Box>
          </Box>

          {/* ===== TRUST SECTION ===== */}
          <Box
            sx={{
              display: "flex",
              p: 2,
              borderRadius: 3,
              bgcolor: "#eff6ff",
              gap: 1.5,
              alignItems: "flex-start",
            }}
          >
            <VerifiedUserIcon
              sx={{
                color: "#2563eb",
                fontSize: 20,
                flexShrink: 0,
                mt: 0.3,
              }}
            />
            <Typography variant="body2" sx={{ color: "#1d4ed8" }}>
              Funds go directly toward hardware purchasing and local
              installation training for youth in the parish.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* ===== STATUS BADGE ===== */}
      <Box
        sx={{
          position: "absolute",
          bottom: -16,
          left: 24,
          bgcolor: "success.main",
          color: "white",
          p: 2,
          borderRadius: 3,
          boxShadow: "0 15px 30px rgba(0,0,0,0.15)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            bgcolor: "rgba(255,255,255,0.2)",
            borderRadius: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <BoltIcon />
        </Box>

        <Box>
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255,255,255,0.7)",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            Status
          </Typography>

          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Active Installation
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ProgressCard;
